import React, { useEffect, createElement } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, CheckCircle2 } from 'lucide-react';
import { LeadForm } from '../LeadForm';
import { ProgramCard } from '../ProgramCard';
import { ReviewCard } from '../ReviewCard';
import { ScheduleTable } from '../ScheduleTable';
import { LocationBlock } from '../LocationBlock';
import { FAQAccordion } from '../FAQAccordion';
import { CTABand } from '../CTABand';
interface LocationTemplateProps {
  city: string;
  state?: string;
  distance: string;
  nearbyAreas: string[];
  intro: string;
  whyLocal: string[];
  metaTitle: string;
  metaDescription: string;
}
export function LocationTemplate({
  city,
  state = 'MI',
  distance,
  nearbyAreas,
  intro,
  whyLocal,
  metaTitle,
  metaDescription
}: LocationTemplateProps) {
  useEffect(() => {
    document.title = metaTitle;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', metaDescription);
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = metaDescription;
      document.head.appendChild(meta);
    }
  }, [metaTitle, metaDescription]);
  return <div className="flex flex-col bg-off-white" data-id="element-360">
      {/* Hero Section */}
      <section className="relative bg-ink pt-12 pb-24 lg:pt-20 lg:pb-32 overflow-hidden" data-id="element-361">
        <div className="absolute inset-0 opacity-20" data-id="element-362">
          <img src="https://images.unsplash.com/photo-1555597673-b21d5c935865?auto=format&fit=crop&w=2000&q=80" alt="Taekwondo Background" className="w-full h-full object-cover" data-id="element-363" />
          <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/90 to-transparent" data-id="element-364" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-365">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-366">
            <div className="flex flex-col items-start" data-id="element-367">
              <div className="inline-flex items-center gap-2 bg-storm-red text-white px-4 py-1.5 rounded-full text-sm font-bold uppercase tracking-wide mb-6" data-id="element-368">
                <MapPin className="w-4 h-4" data-id="element-369" />
                Serving {city}, {state}
              </div>
              <h1 className="text-5xl md:text-6xl text-white mb-6 leading-[1.1]" data-id="element-370">
                Taekwondo & Martial Arts Classes Near {city}, {state}
              </h1>
              <p className="text-xl text-off-white mb-8 max-w-xl leading-relaxed" data-id="element-371">
                {intro}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto mb-10" data-id="element-372">
                <Link to="/free-trial" className="btn-primary" data-id="element-373">
                  Claim Your Free Week
                </Link>
                <a href="tel:517-220-0621" className="btn-secondary" data-id="element-374">
                  Call 517-220-0621
                </a>
              </div>
            </div>

            <div className="w-full max-w-md mx-auto lg:mx-0 lg:ml-auto" data-id="element-375">
              <div className="bg-paper p-8 rounded-2xl shadow-2xl border border-subtle-gray relative" data-id="element-376">
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-storm-red text-white px-6 py-2 rounded-full font-bold uppercase tracking-wide whitespace-nowrap shadow-lg" data-id="element-377">
                  Claim Your Free Week
                </div>
                <div className="mt-4" data-id="element-378">
                  <LeadForm buttonText="Claim Free Week" data-id="element-379" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Distance Band */}
      <section className="bg-charcoal text-white py-6" data-id="element-380">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-center gap-3" data-id="element-381">
          <MapPin className="w-6 h-6 text-storm-red" data-id="element-382" />
          <span className="text-lg font-bold uppercase tracking-wide" data-id="element-383">
            Just {distance}
          </span>
        </div>
      </section>

      {/* Why Local Section */}
      <section className="py-24 bg-paper border-b border-subtle-gray" data-id="element-384">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-385">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-386">
            <h2 className="text-4xl md:text-5xl text-ink mb-6" data-id="element-387">
              Why {city} Families Choose Storm
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto" data-id="element-388">
            {whyLocal.map((reason, idx) => <div key={idx} className="flex items-start gap-4 bg-off-white p-6 rounded-2xl border border-subtle-gray" data-id="element-389">
                <CheckCircle2 className="w-8 h-8 text-storm-red shrink-0" data-id="element-390" />
                <p className="text-lg text-ink font-medium" data-id="element-391">{reason}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Programs Section */}
      <section className="py-24 bg-off-white" data-id="element-392">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-393">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-394">
            <h2 className="text-4xl md:text-5xl text-ink mb-6" data-id="element-395">
              Programs for All Ages
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8" data-id="element-396">
            <ProgramCard title="Kids Taekwondo" ageRange="Ages 6+" benefit="Develop Confidence & Discipline" whoItsFor="Structured classes that teach respect, goal setting, and self-defense fundamentals." imageUrl="https://images.unsplash.com/photo-1555597673-b21d5c935865?auto=format&fit=crop&w=800&q=80" href="/kids-taekwondo-classes" data-id="element-397" />
            <ProgramCard title="Adult Taekwondo" ageRange="Adults" benefit="Fitness & Self-Defense" whoItsFor="Beginner-friendly classes focused on fitness, confidence, and practical skills." imageUrl="https://cdn.magicpatterns.com/uploads/t7AgirMmJivJBXyaULHJUS/Adult.png" href="/adult-taekwondo-classes" data-id="element-398" />
            <ProgramCard title="Family Classes" ageRange="All Ages" benefit="Train & Grow Together" whoItsFor="Families looking for a healthy, active hobby they can do on the same mat." imageUrl="https://cdn.magicpatterns.com/uploads/jpxHBCPtqv9Pv8vdpeiuPD/Family.png" href="/family-martial-arts-classes" data-id="element-399" />
          </div>
        </div>
      </section>

      {/* Schedule & Location */}
      <section className="py-24 bg-paper border-y border-subtle-gray" data-id="element-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-401">
          <div className="grid lg:grid-cols-2 gap-16" data-id="element-402">
            <div data-id="element-403">
              <h2 className="text-4xl text-ink mb-8" data-id="element-404">Class Times</h2>
              <ScheduleTable data-id="element-405" />
            </div>
            <div data-id="element-406">
              <h2 className="text-4xl text-ink mb-8" data-id="element-407">Location Details</h2>
              <LocationBlock data-id="element-408" />
            </div>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="py-24 bg-off-white" data-id="element-409">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-410">
          <h2 className="text-4xl md:text-5xl text-ink text-center mb-16" data-id="element-411">
            Trusted by Local Families
          </h2>
          <div className="grid md:grid-cols-3 gap-8" data-id="element-412">
            <ReviewCard quote="My son has been going here for 6 months and his confidence has skyrocketed. The coaches are amazing with the kids." name="Sarah M." label="Parent Review" data-id="element-413" />
            <ReviewCard quote="Great family atmosphere. We started in the family class and it's been the best decision we've made for our health." name="David R." label="Parent Review" data-id="element-414" />
            <ReviewCard quote="The discipline and focus my daughter has learned here translates directly to her schoolwork. Highly recommend Storm!" name="Jennifer L." label="Parent Review" data-id="element-415" />
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-paper border-t border-subtle-gray" data-id="element-416">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-417">
          <h2 className="text-4xl md:text-5xl text-ink text-center mb-16" data-id="element-418">
            Frequently Asked Questions
          </h2>
          <FAQAccordion faqs={[{
          question: `Do you have many students from ${city}?`,
          answer: `Yes! Because of our convenient location in Farmington Hills, we have many families from ${city} who train with us regularly.`
        }, {
          question: 'How does the free one-week trial work?',
          answer: "Simply fill out the form, and we'll contact you to schedule your first visit. You'll get to try our classes for a full week with no obligation to join."
        }, {
          question: 'Does my child need experience?',
          answer: 'Not at all! The vast majority of our new students have no prior martial arts experience. Our beginner classes are designed to teach the fundamentals from day one.'
        }, {
          question: 'Do parents need to buy gear before the trial?',
          answer: 'No gear is needed to start. Just wear comfortable athletic clothing for your trial classes.'
        }]} data-id="element-419" />
        </div>
      </section>

      {/* Internal Links */}
      <section className="py-12 bg-subtle-gray/30 text-center" data-id="element-420">
        <div className="max-w-4xl mx-auto px-4" data-id="element-421">
          <p className="text-gray-warm font-bold mb-4 uppercase tracking-wide" data-id="element-422">
            Also Serving Nearby Communities
          </p>
          <div className="flex flex-wrap justify-center gap-4" data-id="element-423">
            {nearbyAreas.map((area, idx) => <Link key={idx} to={`/${area.toLowerCase().replace(/\s+/g, '-')}-martial-arts`} className="text-ink hover:text-storm-red transition-colors font-medium" data-id="element-424">
                {area}
              </Link>)}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <CTABand headline={`Ready to Try Storm Taekwondo?`} copy={`Join the many families from ${city} who have discovered the benefits of martial arts at Storm Taekwondo. Claim your free week today.`} data-id="element-425" />
    </div>;
}